// @flow
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import * as React from 'react'

import { colors, media } from '@toggl/style'
import { text } from '@toggl/ui'

type Props = {
  value: string,
  showError?: boolean,
  className?: string,
  children: ({ onFocus: () => void, onBlur: () => void }) => React.Node,
}

export function validate(value: string = '') {
  const rules = validateRules(value)

  if (!rules.sizeValid) {
    return '8 or more characters'
  }

  if (!rules.caseValid) {
    return 'lowercase and uppercase letters'
  }

  if (!rules.numberValid) {
    return 'at least one number'
  }

  return null
}

function validateRules(value: string = '') {
  return {
    sizeValid: value.length >= 8,
    caseValid: !!(value.match(/[a-z]/) && value.match(/[A-Z]/)),
    numberValid: !!value.match(/[0-9]/),
  }
}

export default function Rules({
  value,
  showError = false,
  className,
  children,
}: Props) {
  const rules = React.useMemo(() => validateRules(value || ''), [value])

  const { sizeValid, caseValid, numberValid } = rules

  const isValid = sizeValid && caseValid && numberValid

  const [inputFocus, setInputFocus] = React.useState(false)

  const onFocus = React.useCallback(() => {
    setInputFocus(true)
  }, [])

  const onBlur = React.useCallback(() => {
    setInputFocus(false)
  }, [])

  return (
    <RelativeWrapper>
      {inputFocus && (
        <TooltipWrapper valid={isValid} className={className}>
          {!isValid && <p>Please make sure that your password includes:</p>}
          {isValid && <p>Your password is secure!</p>}
          <RulesList>
            <Rule valid={sizeValid} showError={showError}>
              {(!showError || sizeValid) && (
                <Checkbox readOnly checked={sizeValid} />
              )}
              <span>8 or more characters</span>
            </Rule>
            <Rule valid={caseValid} showError={showError}>
              {(!showError || caseValid) && (
                <Checkbox readOnly checked={caseValid} />
              )}
              <span>lowercase and uppercase letters</span>
            </Rule>
            <Rule valid={numberValid} showError={showError}>
              {(!showError || numberValid) && (
                <Checkbox readOnly checked={numberValid} />
              )}
              <span>at least one number</span>
            </Rule>
          </RulesList>
        </TooltipWrapper>
      )}
      {children({ onFocus, onBlur })}
    </RelativeWrapper>
  )
}

const RelativeWrapper = styled<{ children: React.Node }>('div')`
  position: relative;
  margin-top: 25px;

  div {
    margin-top: 0;
  }
`

const TooltipWrapper = styled<{
  valid: boolean,
  children: React.Node,
  className?: string,
}>('div')`
  ${text.paragraph4};
  padding: 17px;
  color: #666;
  position: absolute;
  bottom: 70px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  background-color: ${colors.white};
  border-radius: 10px;
  box-shadow: 0 2px 6px 0 rgba(102, 102, 102, 0.32);
  opacity: ${({ valid }) => (valid ? '0' : '1')};
  transition: ${({ valid }) =>
    valid ? 'opacity 1.4s ease-in-out' : 'opacity 0.1s ease-in'};
  ${media.mq[2]} {
    width: 60%;
  }
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    left: 50%;
    box-sizing: border-box;
    border-color: ${colors.white} transparent transparent transparent;
    border-style: solid;
    border-width: 10px;
    transform: translateX(-50%) translateY(calc(100% - 1px));
    bottom: 0;
  }

  p {
    color: ${({ valid }) => (valid ? colors.green : colors.lightPurple)};
  }

  p,
  li {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }
`

const Checkbox = () => (
  <CheckboxWrapper>
    <svg width="14" height="14" view-box="0 0 14 14">
      <g fill="none" fillRule="evenodd">
        <rect width="14" height="14" fill={colors.green} rx="20" />
        <path
          stroke={colors.white}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M3.5 7.5L6 10l4-6"
        />
      </g>
    </svg>
  </CheckboxWrapper>
)

const CheckboxWrapper = styled.div`
  width: 14px;
  height: 14px;
  margin-right: 10px;
  display: inline-block;
`

const RulesList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const dot = css`
  &::before {
    display: inline-block;
    content: '';
    height: 6px;
    flex: 0 0 6px;
    font-size: 12px;
    border-radius: 10px;
    margin-right: 14px;
    margin-left: 4px;
    background: ${colors.lightPurple};
  }
`

const errorState = css`
  ${dot};

  &::before {
    background: ${colors.red};
  }

  ${CheckboxWrapper} {
    display: none;
  }
`

const neutralState = css`
  ${dot};

  ${CheckboxWrapper} {
    display: none;
  }
`

const Rule = styled.li`
  text-align: left;
  color: ${(props) =>
    props.valid
      ? colors.green
      : props.showError
      ? colors.red
      : colors.lightPurple};

  ${(props) => props.showError && !props.valid && errorState}
  ${(props) => !props.showError && !props.valid && neutralState}
`
